import {logError} from '../fingerprint.js';

// Известные глобалы, которые оставляют WebDriver/Selenium в window.
const WINDOW_TOKENS = [
    '__nightmare',
    'webdriver',
    '_Selenium_IDE_Recorder',
    'callSelenium',
    '_selenium',
];

// Следы automation в document.
const DOCUMENT_TOKENS = [
    '__webdriver_script_fn',
    '__driver_evaluate',
    '__webdriver_evaluate',
    '__selenium_evaluate',
    '__fxdriver_evaluate',
    '__driver_unwrapped',
    '__webdriver_unwrapped',
    '__selenium_unwrapped',
    '__fxdriver_unwrapped',
    '__webdriver_script_func',
    '$cdc_asdjflasutopfhvcZLmcfl_',
    '$chrome_asyncScriptInfo',
    '__lastWatirPrompt',
    '__lastWatirConfirm',
    '__lastWatirAlert',
    '__$webdriverAsyncExecutor',
    '__webdriverFunc',
    'webdriver-evaluate-response',
    'webdriverCommand',
    'selenium-evaluate',
    'webdriver-evaluate',
    'driver-evaluate',
    'ChromeDriverw',
    '_WEBDRIVER_ELEM_CACHE',
    'calledSelenium',
    '_selenium',
];

// Типичные атрибуты, которыми помечаются узлы под WebDriver.
const ATTRIBUTE_TOKENS = [
    'selenium',
    'webdriver',
    'driver',
];

// Вендорные глобалы, чьё наличие может подсказать движок.
const VENDOR_GLOBALS = [
    'chrome',
    'safari',
    '__crWeb',
    '__gCrWeb',
    'yandex',
    '__yb',
    '__ybro',
    '__firefox__',
    '__edgeTrackingPreventionStatistics',
    'webkit',
    'oprt',
    'samsungAr',
    'ucweb',
    'UCShellJava',
    'puffinDevice',
];

const PHANTOM_KEYS = [
    'callPhantom',
    '_phantom',
    'phantom',
];

function createDefaultResult() {
    return {
        automationSignals: {
            summary: {
                detected: false,
                phantomDetected: false,
                vendorGlobals: [],
            },
            details: {
                window: {
                    tokens: [],
                    flags: {},
                },
                document: {
                    tokens: [],
                    flags: {},
                },
                attributes: {
                    tokens: [],
                    flags: {},
                },
                phantom: {
                    callPhantom: false,
                    _phantom: false,
                    phantom: false,
                    any: false,
                },
            },
        },
    };
}

function detectTokens(tokens, target, checker) {
    const flags = {};
    const matches = [];

    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        let hasToken = false;
        try {
            hasToken = checker(target, token);
        } catch {
            hasToken = false;
        }
        flags[token] = Boolean(hasToken);
        if (hasToken) {
            matches.push(token);
        }
    }

    matches.sort();
    return {flags, matches};
}

function detectPhantomSignals(window) {
    const result = {
        callPhantom: false,
        _phantom: false,
        phantom: false,
        any: false,
    };

    for (let i = 0; i < PHANTOM_KEYS.length; i++) {
        const key = PHANTOM_KEYS[i];
        try {
            result[key] = key in window;
        } catch {
            result[key] = false;
        }
    }

    result.any = result.callPhantom || result._phantom || result.phantom;
    return result;
}

function detectVendorGlobals(window) {
    const matches = [];

    for (let i = 0; i < VENDOR_GLOBALS.length; i++) {
        const key = VENDOR_GLOBALS[i];
        try {
            const value = window[key];
            if (value && typeof value === 'object') {
                matches.push(key);
            }
        } catch {
            // Игнорируем ошибки доступа.
        }
    }

    return matches.sort();
}

/**
 * Собирает признаки автоматизации браузера (Selenium, WebDriver и т. п.).
 * @param {Window} window
 * @returns {Promise<IAutomationSignals>}
 */
export default async function collectAutomationSignals(window) {
    const defaultResult = createDefaultResult();
    if (!window) {
        return defaultResult;
    }

    try {
        const document = window.document;

        const windowTokens = detectTokens(WINDOW_TOKENS, window, (target, key) => key in target);
        const documentTokens = detectTokens(DOCUMENT_TOKENS, document, (target, key) => target && key in target);
        const attributeTokens = detectTokens(ATTRIBUTE_TOKENS, document?.documentElement, (target, key) => {
            if (!target || typeof target.getAttribute !== 'function') {
                return false;
            }
            return target.getAttribute(key) !== null;
        });

        const phantomSignals = detectPhantomSignals(window);
        const vendorGlobals = detectVendorGlobals(window);
        const automationDetected = Boolean(
            windowTokens.matches.length ||
            documentTokens.matches.length ||
            attributeTokens.matches.length,
        );

        return {
            automationSignals: {
                summary: {
                    detected: automationDetected,
                    phantomDetected: phantomSignals.any,
                    vendorGlobals,
                },
                details: {
                    window: {
                        tokens: windowTokens.matches,
                        flags: windowTokens.flags,
                    },
                    document: {
                        tokens: documentTokens.matches,
                        flags: documentTokens.flags,
                    },
                    attributes: {
                        tokens: attributeTokens.matches,
                        flags: attributeTokens.flags,
                    },
                    phantom: phantomSignals,
                },
            },
        };
    } catch (error) {
        logError(error);
        return defaultResult;
    }
}
